import React, { useEffect, useState, useMemo } from 'react';
import ReactEChartsCore from 'echarts-for-react';
import * as echarts from 'echarts/core';
import { BarChart, LineChart, PieChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, LegendComponent, TitleComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import AdminLayout from './AdminLayout';
import supabase from '../../supabase/supabase';

echarts.use([BarChart, LineChart, PieChart, GridComponent, TooltipComponent, LegendComponent, TitleComponent, CanvasRenderer]);

const { FiCalendar, FiDownload, FiRefreshCw } = FiIcons;

const FILTER_OPTIONS = [
  { label: 'Today', value: 'today' },
  { label: 'Last 3 Days', value: '3days' },
  { label: 'Last 7 Days', value: '7days' },
  { label: 'Last 30 Days', value: '30days' },
  { label: 'Last 6 Months', value: '6months' },
  { label: 'Last 1 Year', value: '1year' },
  { label: 'All Time', value: 'all' },
  { label: 'Custom Range', value: 'custom' }
];

function getDateRange(filter, customStart, customEnd) {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
  let start;
  switch (filter) {
    case 'today':
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      break;
    case '3days':
      start = new Date(now); start.setDate(start.getDate() - 2); start.setHours(0, 0, 0, 0);
      break;
    case '7days':
      start = new Date(now); start.setDate(start.getDate() - 6); start.setHours(0, 0, 0, 0);
      break;
    case '30days':
      start = new Date(now); start.setDate(start.getDate() - 29); start.setHours(0, 0, 0, 0);
      break;
    case '6months':
      start = new Date(now); start.setMonth(start.getMonth() - 6); start.setDate(1); start.setHours(0, 0, 0, 0);
      break;
    case '1year':
      start = new Date(now); start.setFullYear(start.getFullYear() - 1); start.setDate(1); start.setHours(0, 0, 0, 0);
      break;
    case 'custom':
      start = customStart ? new Date(customStart + 'T00:00:00') : new Date(2020, 0, 1);
      const customEndDate = customEnd ? new Date(customEnd + 'T23:59:59') : end;
      return { start, end: customEndDate };
    default:
      start = new Date(2020, 0, 1);
  }
  return { start, end };
}

function AdminSiteAnalytics() {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('30days');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('site_visits_1750000013')
      .select('*')
      .order('created_at', { ascending: false });
    setVisits(data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const { start: rangeStart, end: rangeEnd } = useMemo(
    () => getDateRange(filter, customStart, customEnd),
    [filter, customStart, customEnd]
  );

  const filteredVisits = useMemo(() => visits.filter((v) => {
    if (!v.created_at) return false;
    const d = new Date(v.created_at);
    return d >= rangeStart && d <= rangeEnd;
  }), [visits, rangeStart, rangeEnd]);

  const uniqueSessions = new Set(filteredVisits.map((v) => v.session_id)).size;
  const totalPageViews = filteredVisits.length;
  const avgPagesPerSession = uniqueSessions > 0 ? (totalPageViews / uniqueSessions).toFixed(1) : 0;

  const pageCounts = {};
  filteredVisits.forEach((v) => {
    const page = v.page_path || '/';
    pageCounts[page] = (pageCounts[page] || 0) + 1;
  });
  const topPages = Object.entries(pageCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10);

  const dayCount = Math.max(1, Math.ceil((rangeEnd - rangeStart) / (1000 * 60 * 60 * 24)));
  const chartDays = Math.min(dayCount, 30);

  const chartDates = Array.from({ length: chartDays }, (_, i) => {
    const d = new Date(rangeEnd);
    d.setDate(d.getDate() - (chartDays - 1 - i));
    return d.toISOString().slice(0, 10);
  });

  const dailyVisits = chartDates.map((day) => ({
    day,
    views: filteredVisits.filter((v) => v.created_at && v.created_at.slice(0, 10) === day).length,
    sessions: new Set(
      filteredVisits.filter((v) => v.created_at && v.created_at.slice(0, 10) === day).map((v) => v.session_id)
    ).size
  }));

  const lineOption = {
    tooltip: { trigger: 'axis' },
    grid: { left: 40, right: 20, top: 30, bottom: 30 },
    legend: { data: ['Page Views', 'Unique Sessions'], top: 0 },
    xAxis: {
      type: 'category',
      data: chartDates.map((d) => d.slice(5)),
      axisLabel: { fontSize: 10, rotate: chartDays > 14 ? 45 : 0 }
    },
    yAxis: { type: 'value', minInterval: 1 },
    series: [
      {
        name: 'Page Views',
        type: 'line',
        data: dailyVisits.map((d) => d.views),
        smooth: true,
        lineStyle: { color: '#0e8f5b', width: 2.5 },
        areaStyle: { color: 'rgba(14,143,91,0.1)' },
        itemStyle: { color: '#0e8f5b' }
      },
      {
        name: 'Unique Sessions',
        type: 'line',
        data: dailyVisits.map((d) => d.sessions),
        smooth: true,
        lineStyle: { color: '#8b5cf6', width: 2.5 },
        areaStyle: { color: 'rgba(139,92,246,0.08)' },
        itemStyle: { color: '#8b5cf6' }
      }
    ]
  };

  const topPagesBarOption = {
    tooltip: { trigger: 'axis' },
    grid: { left: 120, right: 20, top: 10, bottom: 20 },
    xAxis: { type: 'value', minInterval: 1 },
    yAxis: {
      type: 'category',
      data: topPages.map(([page]) => page.length > 18 ? page.slice(0, 18) + '…' : page).reverse(),
      axisLabel: { fontSize: 11 }
    },
    series: [{
      type: 'bar',
      data: topPages.map(([, count]) => count).reverse(),
      itemStyle: { color: '#3b82f6', borderRadius: [0, 6, 6, 0] },
      barWidth: '60%'
    }]
  };

  const downloadReport = () => {
    const rows = [
      ['Date', 'Page Path', 'Session ID', 'Referrer', 'User Agent'],
      ...filteredVisits.map((v) => [
        new Date(v.created_at).toLocaleString(),
        v.page_path,
        v.session_id,
        v.referrer,
        v.user_agent.slice(0, 60)
      ])
    ];
    const csv = rows
      .map((row) => row.map((val) => `"${String(val || '').replaceAll('"', '""')}"`).join(','))
      .join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `site-analytics-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const filterBarStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
    padding: '0 28px',
    marginBottom: '20px'
  };

  const filterBtnStyle = (isActive) => ({
    padding: '6px 14px',
    borderRadius: '8px',
    fontSize: '0.78rem',
    fontWeight: isActive ? 600 : 500,
    border: isActive ? '1.5px solid #0e8f5b' : '1.5px solid #e5e7eb',
    background: isActive ? '#f0fdf4' : 'white',
    color: isActive ? '#0e8f5b' : '#555',
    cursor: 'pointer',
    transition: 'all 200ms',
    whiteSpace: 'nowrap'
  });

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div><h1>Site Analytics</h1><p>Monitor website visitors, traffic, and page engagement</p></div>
        <div className="admin-header-actions">
          <button className="admin-btn secondary" onClick={load}><SafeIcon icon={FiRefreshCw} /> Refresh</button>
          <button className="admin-btn primary" onClick={downloadReport}>
            <SafeIcon icon={FiDownload} /> Download Report
          </button>
        </div>
      </div>

      <div style={filterBarStyle}>
        <SafeIcon icon={FiCalendar} style={{ color: '#6e716e' }} />
        {FILTER_OPTIONS.map((opt) => (
          <button key={opt.value} style={filterBtnStyle(filter === opt.value)} onClick={() => setFilter(opt.value)}>
            {opt.label}
          </button>
        ))}
        {filter === 'custom' && (
          <>
            <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} style={{ padding: '6px 10px', borderRadius: 8, border: '1.5px solid #e5e7eb', fontSize: '0.78rem' }} />
            <span style={{ color: '#6e716e', fontSize: '0.78rem' }}>to</span>
            <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} style={{ padding: '6px 10px', borderRadius: 8, border: '1.5px solid #e5e7eb', fontSize: '0.78rem' }} />
          </>
        )}
      </div>

      {loading ? (
        <div className="admin-loading">Loading site analytics…</div>
      ) : (
        <>
          <div className="admin-stats">
            <div className="admin-stat-card">
              <div className="stat-icon" style={{ color: '#0e8f5b', background: '#eaf7f1' }}>
                <span style={{ fontSize: '1.3rem' }}>👥</span>
              </div>
              <div className="stat-info">
                <div className="stat-value">{uniqueSessions.toLocaleString()}</div>
                <div className="stat-label">Unique Visitors</div>
              </div>
            </div>
            <div className="admin-stat-card">
              <div className="stat-icon" style={{ color: '#3b82f6', background: '#eff6ff' }}>
                <span style={{ fontSize: '1.3rem' }}>👁</span>
              </div>
              <div className="stat-info">
                <div className="stat-value">{totalPageViews.toLocaleString()}</div>
                <div className="stat-label">Total Page Views</div>
              </div>
            </div>
            <div className="admin-stat-card">
              <div className="stat-icon" style={{ color: '#8b5cf6', background: '#f5f3ff' }}>
                <span style={{ fontSize: '1.3rem' }}>📄</span>
              </div>
              <div className="stat-info">
                <div className="stat-value">{avgPagesPerSession}</div>
                <div className="stat-label">Pages per Session</div>
              </div>
            </div>
            <div className="admin-stat-card">
              <div className="stat-icon" style={{ color: '#d97706', background: '#fffbeb' }}>
                <span style={{ fontSize: '1.3rem' }}>📈</span>
              </div>
              <div className="stat-info">
                <div className="stat-value">{Object.keys(pageCounts).length}</div>
                <div className="stat-label">Pages Visited</div>
              </div>
            </div>
          </div>

          <div className="admin-charts-grid">
            <div className="admin-card" style={{ margin: 0 }}>
              <h2>Visitors Over Time</h2>
              <ReactEChartsCore echarts={echarts} option={lineOption} style={{ height: 300 }} />
            </div>
            <div className="admin-card" style={{ margin: 0 }}>
              <h2>Top Pages</h2>
              {topPages.length > 0 ? (
                <ReactEChartsCore echarts={echarts} option={topPagesBarOption} style={{ height: 300 }} />
              ) : (
                <div className="admin-empty" style={{ padding: '40px 20px' }}>
                  <p>No page visit data yet</p>
                  <span className="small-text">Data will appear as visitors browse the site</span>
                </div>
              )}
            </div>
          </div>

          {topPages.length > 0 && (
            <div className="admin-card">
              <h2>Most Visited Pages</h2>
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Page</th>
                      <th>Views</th>
                      <th>% of Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topPages.map(([page, count], idx) => (
                      <tr key={page}>
                        <td>{idx + 1}</td>
                        <td><span className="mono">{page}</span></td>
                        <td><strong>{count}</strong></td>
                        <td>{((count / totalPageViews) * 100).toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
}

export default AdminSiteAnalytics;